import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, Star, Loader2, MapPin } from 'lucide-react';
import RestCard from './RestCard';
import type { Restaurant } from '../../Manager/restaurant.types';
import MapOverlay from '../../MapOverlay';


interface FilterOption {
  id: string;
  label: string;
  type: 'category' | 'rating' | 'price' | 'open';
  value?: number | string;
}

// Main Component
interface RestaurantListingProps {
  title: string;
  fetchRestaurants: (page: number, filters: Record<string, any>) => Promise<any>;
  onBack?: () => void;
  onRestaurantClick?: (id: string, name: string) => void;
}

const RestaurantListing: React.FC<RestaurantListingProps> = ({
  title,
  fetchRestaurants,
  onBack,
  onRestaurantClick
}) => {
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Filter options
    const filterOptions: FilterOption[] = [
    // { id: 'open-now', label: 'Open Now', type: 'open' },
    { id: 'romantic', label: 'Romantic', type: 'category' },
    { id: 'vegan', label: 'Vegan', type: 'category' },
    { id: 'rating-3', label: '3+ Stars', type: 'rating', value: 3 },
    { id: 'rating-4', label: '4+ Stars', type: 'rating', value: 4 },
    { id: 'price-mid', label: 'Mid-Range', type: 'price', value: 'mid-range' },
    { id: 'price-premium', label: 'Premium', type: 'price', value: 'premium' },
    { id: 'price-fine', label: 'Fine Dining', type: 'price', value: 'fine-dining' },
    ];

  // Render star rating in filter button
  const renderFilterStars = (count: number) => (
    <div className="flex">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={12}
          className={star <= count ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
        />
      ))}
    </div>
  );

  // Load restaurants
  const loadRestaurants = useCallback(async (pageNum: number, reset: boolean = false) => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      const filters: Record<string, any> = {};
      activeFilters.forEach(filterId => {
        const filter = filterOptions.find(f => f.id === filterId);
        if (filter) {
          if (filter.type === 'rating') {
            filters.minRating = filter.value;
          } else if (filter.type === 'price') {
            filters.priceLevel = filter.value;
          } else if (filter.type === 'open') {
            filters.openNow = true;
          } else {
            filters[filter.id] = true;
          }
        }
      });

      const { pagination, restaurants} = await fetchRestaurants(pageNum, filters);
      
      setRestaurants(prev => reset ? restaurants : [...prev, ...restaurants]);
      setHasMore(pagination.hasMore);
      setPage(pagination.page + 1);
    } catch (error) {
      console.error('Error loading restaurants:', error);
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  }, [activeFilters, fetchRestaurants, isLoading]);

  // Initial load
  useEffect(() => {
    loadRestaurants(1, true);
  }, []);

  // Reload when filters change
  useEffect(() => {
    if (!isInitialLoading) {
      loadRestaurants(1, true);
    }
  }, [activeFilters]);

  // Infinite scroll
  useEffect(() => {
    if (!hasMore || isLoading) return;

    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !isLoading) {
        loadRestaurants(page + 1, false);
      }
    }, options);

    if (loadMoreTriggerRef.current) {
      observerRef.current.observe(loadMoreTriggerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoading, page, loadRestaurants]);

  // Toggle filter
  const toggleFilter = (filterId: string) => {
    setActiveFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(filterId)) {
        newFilters.delete(filterId);
      } else {
        // Remove other rating filters if selecting a rating
        const filter = filterOptions.find(f => f.id === filterId);
        if (filter?.type === 'rating') {
          filterOptions.filter(f => f.type === 'rating').forEach(f => newFilters.delete(f.id));
        }
        // Remove other price filters if selecting a price
        if (filter?.type === 'price') {
          filterOptions.filter(f => f.type === 'price').forEach(f => newFilters.delete(f.id));
        }
        newFilters.add(filterId);
      }
      return newFilters;
    });
  };

  return (
    <div className="bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-20 py-2 bg-white border-b border-gray-200 shadow-sm">
        <div className="flex items-center px-4">
          <button
            onClick={onBack}
            className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="flex-1 text-center text-lg font-semibold text-gray-900 pr-10">
            {title}
          </h1>
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          <div className="flex gap-2 px-4 min-w-max">
            {filterOptions.map((filter) => (
              <button
                key={filter.id}
                onClick={() => toggleFilter(filter.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  activeFilters.has(filter.id)
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.type === 'rating' && filter.value ? (
                  <div className="flex items-center gap-1">
                    {/* @ts-ignore */}
                    {renderFilterStars(filter.value)}
                  </div>
                ) : (
                  filter.label
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Restaurant List */}
      <div>
        {isInitialLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-3" />
            <p className="text-gray-600">Loading restaurants...</p>
          </div>
        ) : restaurants?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-gray-600 text-center">
              We couldn’t find any restaurants. Try adjusting the filters or retrying.     
                       {activeFilters.size > 0 && <br />}
              {activeFilters.size > 0 && <span className="text-sm">Check connection and retry</span>}
            </p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {restaurants?.map((restaurant) => (
                <RestCard
                  key={restaurant._id}
                  restaurant={restaurant}
                  onCardClick={(id, name) => onRestaurantClick?.(id, name)}
                />
              ))}
            </div>

            {/* Loading indicator */}
            {hasMore && (
              <div ref={loadMoreTriggerRef} className="flex justify-center py-8">
                {isLoading && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Loading more...</span>
                  </div>
                )}
              </div>
            )}

            {/* End message */}
            {!hasMore && restaurants?.length > 0 && (
              <div className="flex justify-center py-8">
                <p className="text-gray-500 text-sm">You've reached the end</p>
              </div>
            )}
            {/* Floating Map Button */}
            {!isLoading && restaurants.length > 0 && 
            <>
            <div className="sticky bottom-0 left-0 right-0 z-40">
                <div className="bg-gradient-to-t from-black/60 via-black/20 to-transparent pt-12 pb-6 px-4">
                <button 
                    className="w-full bg-gray-900 text-white py-4 rounded-full font-semibold shadow-2xl flex items-center justify-center gap-2 active:bg-gray-800 transition"    onClick={() => setShowMap(true)}        
                >
                    <MapPin size={20} />
                    View Map
                </button>
                </div>
            </div>

            <MapOverlay
                show={showMap}
                onClose={() => setShowMap(false)}
                restaurants={restaurants}
            /> 
            </>
            }
          </>
        )}
      </div>
    </div>
  );
};

export default RestaurantListing;